// components/Chat/ChatView.js

import React, { useState, useRef, useEffect } from 'react';
import styles from '../../ChatApp.module.css';
import { PaperclipIcon, MicIcon, StopIcon } from '../../utils/AppHelpers';
import { FloatingCartButton, SidebarCart } from '../Cart/CartButtons';
import ProductSelection from './ProductSelection';


const ChatView = ({ 
    messages, 
    onTextSubmit, 
    onImageUpload, 
    onAddItemsToCart, 
    onQuickAdd, 
    onImageClick, 
    cart, 
    onUpdateQuantity, 
    onRemoveItem, 
    isMobile, 
    isLoading,
    onAudioUpload, 
    isRecording,
    onParseDraft,
    onCheckout
}) => {
    const [inputValue, setInputValue] = useState('');
    const [agentDisplayName, setAgentDisplayName] = useState('AirGrocer AI');
    
    const messagesEndRef = useRef(null);
    const fileInputRef = useRef(null);
    const textAreaRef = useRef(null);

    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
        const lastAgentMessage = [...messages].reverse().find(m => m.role !== 'user');
        if (lastAgentMessage) setAgentDisplayName(lastAgentMessage.role);
    }, [messages]);

    const handleFormSubmit = (e) => {
        e.preventDefault();
        if (!inputValue.trim() || isLoading || isRecording) return;
        onTextSubmit(inputValue, agentDisplayName);
        setInputValue('');
        if (textAreaRef.current) textAreaRef.current.style.height = 'auto';
    };
    
    const handleTextareaChange = (e) => {
        setInputValue(e.target.value);
        if (textAreaRef.current) {
            textAreaRef.current.style.height = 'auto';
            textAreaRef.current.style.height = `${Math.min(textAreaRef.current.scrollHeight, 150)}px`;
        }
    };
    
    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleFormSubmit(e);
        }
    };
    
    const cartItemCount = cart.items.reduce((acc, item) => acc + item.quantity, 0);

    return (
        <div className={styles.container}>
            {isMobile && (
                <FloatingCartButton 
                    itemCount={cartItemCount}
                    onClick={() => onImageClick(true)}
                />
            )}
            
            <div className={styles.chatArea}>
                <div className={styles.messages}>
                    <div className={styles.messagesContainer}>
                        {messages.map((msg, index) => {
                            if (msg.type === 'product_selection') {
                                return (
                                    <ProductSelection
                                        key={msg.id}
                                        products={msg.products}
                                        onAddToCart={onAddItemsToCart}
                                        onQuickAdd={onQuickAdd}
                                        onImageClick={onImageClick}
                                    />
                                );
                            }
                            return (
                                <div
                                    key={index}
                                    className={msg.role === 'user' ? styles.userMessage : styles.agentMessage}
                                    style={{position: 'relative', paddingBottom: msg.role !== 'user' ? '30px' : '12px'}} 
                                >
                                    {msg.role !== 'user' && (
                                        <span className={styles.agentName}>{msg.role}</span>
                                    )}
                                    {msg.content}
                                </div>
                            );
                        })}
                    </div>
                    <div ref={messagesEndRef} />
                </div>
                
                <form onSubmit={handleFormSubmit} className={styles.inputForm}>
                    <input
                        type="file"
                        ref={fileInputRef}
                        onChange={(e) => onImageUpload(e.target.files[0])}
                        style={{ display: 'none' }}
                        accept="image/*"
                        disabled={isLoading || isRecording}
                    />
                    <button type="button" onClick={() => fileInputRef.current.click()} className={styles.iconButton} disabled={isLoading || isRecording} title="Upload an image">
                        <PaperclipIcon />
                    </button>
                    
                    <button type="button" onClick={onAudioUpload} className={styles.iconButton} disabled={isLoading} title={isRecording ? "Stop recording" : "Record voice"}>
                        {isRecording ? <StopIcon /> : <MicIcon />}
                    </button>
                    
                    <textarea 
                        ref={textAreaRef}
                        value={inputValue} 
                        onChange={handleTextareaChange} 
                        onKeyDown={handleKeyDown}
                        placeholder={isRecording ? "Recording..." : "Ask about products or paste an order..."} 
                        className={styles.inputField}
                        disabled={isLoading || isRecording}
                        rows={1}
                    />
                    
                    <button type="submit" className={styles.button} disabled={isLoading || isRecording}>{isLoading ? '...' : 'Send'}</button>
                </form>
            </div>
        
            {!isMobile && (
                <SidebarCart 
                    cart={cart}
                    onUpdateQuantity={onUpdateQuantity}
                    onRemoveItem={onRemoveItem}
                    onImageClick={onImageClick}
                    onCheckout={onCheckout}
                    onParseDraft={onParseDraft}
                />
            )}
        </div>
    );
};

export default ChatView;